import type { AxiosInstance, AxiosRequestConfig } from 'axios'

import type {
  ProfileOverrideBody,
  ProfileOverrideResult,
  ProfilesPayload,
  RunRequestBody,
  RunResultsPage,
  RunUnlockResult,
  RunsPayload,
  ScheduleWriteResult,
  SchedulesPayload,
  MonthlyQuotaDto,
} from '../models/impact.dto'
import { toMonthlyQuota } from '../models/impact.dto'
import type {
  AnalysisRun,
  AnalysisSchedule,
  CausalProfile,
  MonthlyQuota,
} from '../models/impact.types'
import { parseCausalBody } from '../service/causalBodySanitizer'

import { ApiError, appHttp, unwrap } from '@/shared/http'

export const causalHttp = appHttp

export interface CausalScope {
  accountId: number
  workspaceId: number
}

function requestConfig(signal?: AbortSignal, idempotencyKey?: string): AxiosRequestConfig {
  const config: AxiosRequestConfig = {
    suppressGlobalErrorHandler: true,
    meta: { suppressGlobalErrorHandler: true },
  }
  if (signal) config.signal = signal
  if (idempotencyKey) config.headers = { 'Idempotency-Key': idempotencyKey }
  return config
}

function base(scope: CausalScope): string {
  return `/accounts/${scope.accountId}/workspaces/${scope.workspaceId}/workflow/causal-single`
}

function listFrom<T>(payload: T[] | Record<string, T[]>, key: string): T[] {
  if (Array.isArray(payload)) return payload
  return Array.isArray(payload[key]) ? payload[key] : []
}

/**
 * The effective campaign-budget profile currently ships with the ad-set description copied into
 * it on staging. Keep the picker truthful until that backend profile text is corrected; this is
 * deliberately scoped to the known profile slug rather than rewriting arbitrary user copy.
 */
function normalizeProfileDescription(profile: CausalProfile): CausalProfile {
  if (profile.slug !== 'campaign-budget' || !profile.description) return profile
  return {
    ...profile,
    description: profile.description.replace(/ad\s*set/giu, (match) =>
      match.charAt(0) === match.charAt(0).toUpperCase() ? 'Campaign' : 'campaign',
    ),
  }
}

async function featureRequest<T>(request: Promise<T>): Promise<T> {
  try {
    return await request
  } catch (cause) {
    throw ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
  }
}

/** ORP-18716's minimum read boundary. Later module-13 tickets extend this same API class. */
export class CausalApi {
  constructor(private readonly http: AxiosInstance = causalHttp) {}

  async getProfiles(scope: CausalScope, signal?: AbortSignal): Promise<CausalProfile[]> {
    const response = await featureRequest(
      this.http.get(`${base(scope)}/profiles/effective/`, requestConfig(signal)),
    )
    return listFrom(unwrap<ProfilesPayload>(response), 'profiles').map(normalizeProfileDescription)
  }

  async getSchedules(scope: CausalScope, signal?: AbortSignal): Promise<AnalysisSchedule[]> {
    const response = await featureRequest(
      this.http.get(`${base(scope)}/schedules/`, requestConfig(signal)),
    )
    return listFrom(unwrap<SchedulesPayload>(response), 'schedules')
  }

  async getRuns(scope: CausalScope, signal?: AbortSignal): Promise<AnalysisRun[]> {
    const response = await featureRequest(
      this.http.get(`${base(scope)}/results/`, requestConfig(signal)),
    )
    return listFrom(unwrap<RunsPayload>(response), 'results')
  }

  async getQuota(scope: CausalScope, signal?: AbortSignal): Promise<MonthlyQuota> {
    const response = await featureRequest(
      this.http.get(`${base(scope)}/quota/`, requestConfig(signal)),
    )
    return toMonthlyQuota(unwrap<MonthlyQuotaDto>(response))
  }

  async createProfileOverride(
    scope: CausalScope,
    body: ProfileOverrideBody,
  ): Promise<ProfileOverrideResult> {
    const response = await featureRequest(
      this.http.post(`${base(scope)}/profiles/overrides/`, body, requestConfig()),
    )
    return unwrap(response)
  }

  async updateProfileOverride(
    scope: CausalScope,
    overrideId: number,
    body: ProfileOverrideBody,
  ): Promise<ProfileOverrideResult> {
    const response = await featureRequest(
      this.http.put(`${base(scope)}/profiles/overrides/${overrideId}/`, body, requestConfig()),
    )
    return unwrap(response)
  }

  async getRunResults(
    scope: CausalScope,
    executionId: string,
    options: { page?: number; pageSize?: number; signal?: AbortSignal } = {},
  ): Promise<RunResultsPage> {
    const response = await featureRequest(
      this.http.get(`${base(scope)}/runs/${encodeURIComponent(executionId)}/results/`, {
        ...requestConfig(options.signal),
        // The causal model can emit bare NaN/Infinity for an under-determined effect estimate —
        // neither is valid JSON, so this sanitises before axios's default parser ever sees it.
        transformResponse: [parseCausalBody],
        params: { view: 'full', page: options.page ?? 1, page_size: options.pageSize ?? 20 },
      }),
    )
    return unwrap(response)
  }

  /**
   * Unlock a partial run's locked rows with the remaining balance (legacy `unlockResults`,
   * New Front `e995d7f`). Resolves to the number of rows unlocked — 0 when none could be.
   */
  async unlockRun(scope: CausalScope, executionId: string): Promise<number> {
    const response = await featureRequest(
      this.http.post(
        `${base(scope)}/runs/${encodeURIComponent(executionId)}/unlock/`,
        {},
        requestConfig(),
      ),
    )
    return Number(unwrap<RunUnlockResult>(response)?.credits?.unlocked) || 0
  }

  async createSchedule(
    scope: CausalScope,
    body: RunRequestBody,
    idempotencyKey: string,
  ): Promise<ScheduleWriteResult> {
    const response = await featureRequest(
      this.http.post(`${base(scope)}/schedules/`, body, requestConfig(undefined, idempotencyKey)),
    )
    return unwrap(response)
  }

  async updateSchedule(
    scope: CausalScope,
    scheduleId: number,
    body: RunRequestBody,
  ): Promise<AnalysisSchedule> {
    const response = await featureRequest(
      this.http.put(`${base(scope)}/schedules/${scheduleId}/`, body, requestConfig()),
    )
    return unwrap(response)
  }

  async deleteSchedule(scope: CausalScope, scheduleId: number): Promise<void> {
    await featureRequest(
      this.http.delete(`${base(scope)}/schedules/${scheduleId}/`, requestConfig()),
    )
  }

  async pauseSchedule(scope: CausalScope, scheduleId: number): Promise<AnalysisSchedule> {
    return this.scheduleAction(scope, scheduleId, 'pause')
  }

  async resumeSchedule(scope: CausalScope, scheduleId: number): Promise<AnalysisSchedule> {
    return this.scheduleAction(scope, scheduleId, 'resume')
  }

  async runScheduleNow(
    scope: CausalScope,
    scheduleId: number,
    idempotencyKey: string,
  ): Promise<ScheduleWriteResult> {
    return this.scheduleAction(scope, scheduleId, 'run-now', idempotencyKey)
  }

  private async scheduleAction<T extends AnalysisSchedule | ScheduleWriteResult>(
    scope: CausalScope,
    scheduleId: number,
    action: 'pause' | 'resume' | 'run-now',
    idempotencyKey?: string,
  ): Promise<T> {
    const response = await featureRequest(
      this.http.post(
        `${base(scope)}/schedules/${scheduleId}/${action}/`,
        undefined,
        requestConfig(undefined, idempotencyKey),
      ),
    )
    return unwrap(response) as T
  }
}

export const causalApi = new CausalApi()
